import { ActionIcon, Divider, Tooltip } from '@mantine/core';
import { Pencil, Trash2 } from 'lucide-react';
import { KeyboardEvent, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { DeleteAppointment } from '@/components/AppointmentScheduler/delete-appointment.tsx';
import Icon from '@/components/Icon.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import useCheckInData from '@/hooks/useCheckInData.ts';

import { Appointment, CollectionActionScope, DailyAppointment } from '@/models/appointment/Appointment.ts';
import {
    APPOINTMENT_CANCEL_OPTION_TRANSLATIONS,
    CancelAppointmentOptions,
} from '@/models/appointment/CancelAppointmentOptions.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import {
    generateAppointmentKey,
    getAppointmentCancelReasonTranslation,
    getInstanceId,
    isCanceledAppointment,
    isChainMode,
    isCheckedOut,
    isFutureAppointment,
    isSeriesMode,
    titleAfterId,
} from '@/utils/appointmentUtils.ts';
import { getWeekRangeFromDate } from '@/utils/date-range-utils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { cn } from '@/utils/styleUtils.ts';

import { AppointmentPill, getCollectionPillColor } from '../appointment-pill.tsx';

interface Props {
    appointment: Appointment;
    onEdit?: (appointment: Appointment) => void;
    onDelete?: (
        appointment: Appointment,
        scope: CollectionActionScope | undefined,
        cancelOption: CancelAppointmentOptions
    ) => void;
    onAppointmentClick?: (appointment: DailyAppointment) => void;
    practitionerName: string;
    noSideBorders?: boolean;
    isSeriesUnfinished?: boolean;
}

export const CollapsedAppointment = ({
    appointment,
    onEdit,
    onDelete,
    onAppointmentClick,
    practitionerName,
    noSideBorders,
    isSeriesUnfinished,
}: Props) => {
    const location = useLocation();
    const [showDelete, setShowDelete] = useState(false);
    const isFutureApp = isFutureAppointment(dayjs(), appointment);
    const isNoShow = !!appointment.is_no_show;
    const isCanceled = isCanceledAppointment(appointment);

    const { check_in_data: checkInData } = useCheckInData();
    const checkoutData = checkInData?.get(generateAppointmentKey(getInstanceId(appointment), appointment.id));
    const checkedOut = isCheckedOut(checkoutData?.appointment?.checkout_status);

    const { is_week_view_enabled: isWeekViewEnabled, is_weekend_visible: isWeekendVisible } = useCalendarStore(
        useShallow((state) => ({
            is_week_view_enabled: state.is_week_view_enabled,
            is_weekend_visible: state.is_weekend_visible,
        }))
    );

    const handleAppointmentClick = (): void => {
        if (isCanceled) return; // Disable click for cancelled appointments

        if (onAppointmentClick) {
            onAppointmentClick(appointment as DailyAppointment);
        }

        if (location.pathname === '/calendar-view') {
            const appointmentDate = dayjs(appointment.start).toDate();

            if (isWeekViewEnabled) {
                // In week view, navigate to the week containing the appointment
                const [startDate, endDate] = getWeekRangeFromDate(appointmentDate, isWeekendVisible);
                if (startDate && endDate) {
                    useFilterStore.setState({
                        start_date: startDate,
                        end_date: endDate,
                    });
                }
            } else {
                // In day view, navigate to the specific day
                useFilterStore.setState({
                    start_date: appointmentDate,
                    end_date: appointmentDate,
                });
            }
        }
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleAppointmentClick();
        }
    };

    const handleDelete = (scope: CollectionActionScope | undefined, reason: CancelAppointmentOptions): void => {
        setShowDelete(false);
        if (onDelete) {
            onDelete(appointment, scope, reason);
        }
    };

    const hasInteractiveBehavior: boolean =
        !isCanceled && (!!onAppointmentClick || location.pathname === '/calendar-view');
    const isInteractive = hasInteractiveBehavior || isNoShow;
    const cancelReasonLabel = getAppointmentCancelReasonTranslation(appointment.cancel_reason);

    // Set cancelLabel based on cancellation info (always a string if canceled or no-show)
    let cancelLabel: string | undefined;
    if (isCanceled || isNoShow) {
        if (cancelReasonLabel) {
            cancelLabel = `Abgesagt: ${cancelReasonLabel}`;
        } else if (isNoShow) {
            cancelLabel = `Abgesagt: ${APPOINTMENT_CANCEL_OPTION_TRANSLATIONS[CancelAppointmentOptions.NO_SHOW]}`;
        } else {
            cancelLabel = 'Der Termin wurde storniert';
        }
    }

    return (
        <div
            key={appointment.id}
            className={cn(
                'flex items-start justify-between gap-4 py-4',
                isCanceled || isNoShow ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white',
                hasInteractiveBehavior && 'cursor-pointer hover:bg-gray-50',
                isNoShow && 'cursor-pointer hover:bg-red-100',
                isCanceled && 'cursor-default',
                noSideBorders ? 'border-y px-4' : 'rounded-lg border px-6',
                isSeriesUnfinished && !isFutureApp && !isCanceled && !isNoShow && 'bg-gray-100 text-gray-400'
            )}
            onClick={handleAppointmentClick}
            onKeyDown={handleKeyDown}
            role={isInteractive ? 'button' : undefined}
            tabIndex={isInteractive ? 0 : undefined}
        >
            <div className="flex-1">
                <Paragraph className={cn((isCanceled || isNoShow) && 'text-gray-600')}>
                    {appointment?.collection_data && (
                        <span className="mr-2">
                            <AppointmentPill
                                text={`${appointment.collection_data.position}/${appointment.collection_data.total_appointments}`}
                                color={getCollectionPillColor(appointment)}
                                variant="filled"
                            />
                        </span>
                    )}
                    {`${titleAfterId(appointment.name)} (${appointment.duration.value} Min), ${dayjs(appointment.start).format('DD.MM.YYYY')} um ${dayjs(appointment.start).format('HH:mm')} mit ${practitionerName}`}
                </Paragraph>
                {cancelLabel && (
                    <div className="mt-1 flex items-center gap-2">
                        <span className="text-sm text-red-700">{cancelLabel}</span>
                    </div>
                )}
            </div>
            {!showDelete && isFutureApp && !isCanceled && !checkedOut && (onEdit || onDelete) && (
                <div className="flex items-center gap-3">
                    {onDelete && (
                        <Tooltip label="Termin löschen">
                            <ActionIcon
                                variant="transparent"
                                aria-label="delete-appointment-icon"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent the appointment click handler from firing
                                    setShowDelete(true);
                                }}
                                className="bg-transparent"
                            >
                                <Icon
                                    icon={Trash2}
                                    color="text-red-600 hover:text-red-500"
                                    className="cursor-pointer"
                                />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {onEdit && (
                        <Tooltip label="Termin bearbeiten">
                            <ActionIcon
                                variant="transparent"
                                aria-label="edit-appointment-icon"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent the appointment click handler from firing
                                    onEdit(appointment);
                                }}
                                className="bg-transparent"
                            >
                                <Icon icon={Pencil} color="text-black" className="cursor-pointer" />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </div>
            )}
            {showDelete && onDelete && (
                <>
                    <Divider orientation="vertical" />
                    <div className="flex w-full flex-col">
                        <DeleteAppointment
                            onDelete={handleDelete}
                            onCancel={() => setShowDelete(false)}
                            isSeriesMode={isSeriesMode(appointment)}
                            isChainMode={isChainMode(appointment)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
